import React from 'react';

/**
 * Loading Spinner Component - Reusable spinner
 */
function LoadingSpinner() {
  return (
    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );
}

/**
 * Primary Button - Neon Yellow CTA
 * Use for main actions, CTAs, and primary user flows
 * Works on both light and dark backgrounds
 */
export function PrimaryButton({ 
  children, 
  onClick, 
  disabled = false, 
  isLoading = false,
  className = '',
  size = 'md',
  ...props 
}) {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center
        bg-zapt-yellow text-zapt-navy
        rounded-xl font-semibold
        hover:bg-zapt-yellow/90
        transition-all duration-200
        shadow-lg shadow-zapt-yellow/25
        hover:shadow-xl hover:shadow-zapt-yellow/40
        hover:scale-105
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-lg
        ${sizeClasses[size]}
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <>
          <LoadingSpinner />
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Secondary Button - Purple
 * Use for secondary actions, alternative CTAs
 */
export function SecondaryButton({ 
  children, 
  onClick, 
  disabled = false, 
  isLoading = false,
  className = '',
  size = 'md',
  variant = 'filled', // 'filled' or 'outline'
  ...props 
}) {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl'
  };

  const variantClasses = variant === 'outline'
    ? 'bg-transparent border-2 border-zapt-purple text-zapt-purple hover:bg-zapt-purple/10'
    : 'bg-zapt-purple text-white hover:bg-zapt-purple/90';

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center
        ${variantClasses}
        rounded-xl font-semibold
        transition-all duration-200
        shadow-md shadow-zapt-purple/20
        hover:shadow-lg hover:shadow-zapt-purple/30
        hover:scale-105
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        ${sizeClasses[size]}
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Tertiary Button - Ghost/Text
 * Use for less prominent actions, cancel buttons
 */
export function TertiaryButton({ 
  children, 
  onClick, 
  disabled = false, 
  isLoading = false,
  className = '',
  size = 'md',
  dark = false, // Use for dark backgrounds
  ...props 
}) {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl'
  };

  const colorClasses = dark
    ? 'border-white/30 text-gray-200 hover:bg-white/10 hover:border-white/50'
    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400';

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center
        bg-transparent border-2 ${colorClasses}
        rounded-xl font-medium
        transition-all duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        ${sizeClasses[size]}
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <>
          <LoadingSpinner />
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
}

/**
 * Danger Button - Red
 * Use for destructive actions (delete, remove, etc.)
 */
export function DangerButton({ 
  children, 
  onClick, 
  disabled = false, 
  isLoading = false,
  className = '',
  size = 'md',
  ...props 
}) {
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled || isLoading}
      className={`
        inline-flex items-center justify-center
        bg-red-600 text-white
        rounded-xl font-semibold
        hover:bg-red-700
        transition-all duration-200
        shadow-md shadow-red-500/20
        hover:shadow-lg hover:shadow-red-500/30
        hover:scale-105
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
        ${sizeClasses[size]}
        ${className}
      `}
      {...props}
    >
      {isLoading ? (
        <>
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Loading...
        </>
      ) : (
        children
      )}
    </button>
  );
}

